import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  FileText, RefreshCw, CheckCircle, Clock, AlertCircle,
  BarChart2, Image as ImageIcon, Trash2, X, Loader2,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ObserverNav from '../components/ObserverNav';
import ObserverMonthlyReports from './ObserverMonthlyReports';
import InsightCard from '../components/InsightCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverReportsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [insightCardReport, setInsightCardReport] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const activeTab = searchParams.get('tab') || 'daily';
  const setTab = (t) => setSearchParams({ tab: t });

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    fetchReports();
  }, [navigate]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('observer_token');
      const response = await axios.get(`${BACKEND_URL}/api/observer/daily-reports?token=${token}`);
      setReports(response.data.reports || []);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({ title: "Error", description: "Failed to load reports", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(reports.map((r) => r.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const runBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('observer_token');
      const { data } = await axios.post(
        `${BACKEND_URL}/api/observer/reports/bulk-delete?token=${token}`,
        { report_ids: Array.from(selectedIds) },
      );
      toast({
        title: 'Reports deleted',
        description: `${data.deleted} report${data.deleted === 1 ? '' : 's'} removed`
          + (data.insights_hidden ? ` (+${data.insights_hidden} linked parent insight${data.insights_hidden === 1 ? '' : 's'})` : '')
          + '. Backups are kept — an admin can restore.',
      });
      setConfirmOpen(false);
      await fetchReports();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'flagged': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'reviewed': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      default: return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'flagged': return 'bg-red-100 text-red-700';
      case 'reviewed': return 'bg-blue-100 text-blue-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
            <p className="text-gray-600">Your session reports and monthly summaries</p>
          </div>
          {activeTab === 'daily' && (
            <div className="flex items-center gap-2">
              {reports.length > 0 && (
                selectedIds.size === reports.length ? (
                  <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="reports-clear-selection-btn">
                    Clear
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={selectAll} data-testid="reports-select-all-btn">
                    Select all
                  </Button>
                )
              )}
              <Button variant="outline" onClick={fetchReports}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b">
          <button
            onClick={() => setTab('daily')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'daily' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <FileText className="w-4 h-4" /> My Reports
          </button>
          <button
            onClick={() => setTab('monthly')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'monthly' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <BarChart2 className="w-4 h-4" /> Monthly Reports
          </button>
        </div>

        {activeTab === 'monthly' && <ObserverMonthlyReports />}

        {activeTab === 'daily' && (reports.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No reports submitted yet</p>
              <p className="text-gray-400">Complete a session to submit your first report</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 pb-24">
            {reports.map(report => {
              const isVideoCall = report.source === 'video_call';
              const isAiCall = isVideoCall || report.source === 'observer_call_report' || !!report.report_data;
              const rd = report.report_data || {};
              const mood = report.child_mood || rd.mood || 'neutral';
              const engagementObj = report.engagement_score || rd.engagement_score;
              const engagementText = isAiCall
                ? (engagementObj?.score !== undefined ? `${engagementObj.score}/100 · ${engagementObj.label || ''}` : '—')
                : (report.engagement_level || '—');
              const engagementClass = isAiCall
                ? (engagementObj?.score >= 60 ? 'bg-green-100 text-green-700'
                   : engagementObj?.score >= 40 ? 'bg-amber-100 text-amber-700'
                   : 'bg-red-100 text-red-700')
                : (report.engagement_level === 'high' ? 'bg-green-100 text-green-700'
                   : report.engagement_level === 'medium' ? 'bg-amber-100 text-amber-700'
                   : 'bg-red-100 text-red-700');
              const ogs = report.overall_growth_score || rd.overall_growth_score || {};
              const growthLabel = ({
                balanced: 'Balanced Growth',
                moderate: 'Moderate Growth',
                limited: 'Limited Growth',
              })[ogs.rating] || (ogs.rating || '—');
              const growthClass = ({
                balanced: 'bg-blue-100 text-blue-700',
                moderate: 'bg-amber-100 text-amber-700',
                limited: 'bg-red-100 text-red-700',
              })[ogs.rating] || 'bg-gray-100 text-gray-700';
              const moodClass =
                mood === 'happy' || mood === 'excited' ? 'bg-green-100 text-green-700' :
                mood === 'calm' ? 'bg-blue-100 text-blue-700' :
                mood === 'curious' || mood === 'engaged' ? 'bg-violet-100 text-violet-700' :
                mood === 'upset' || mood === 'anxious' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700';
              const isSelected = selectedIds.has(report.id);
              return (
              <Card
                key={report.id}
                className={`border-0 shadow-sm hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-purple-400' : ''}`}
                data-testid={`report-card-${report.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(report.id)}
                        className="mt-1 shrink-0"
                        data-testid={`report-select-${report.id}`}
                        aria-label={`Select report for ${report.child_name}`}
                      />
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center shrink-0">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold flex items-center gap-2 flex-wrap">
                          {report.child_name}
                          {isVideoCall && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-100 text-orange-700 border border-orange-200">
                              VIDEO CALL
                            </span>
                          )}
                          {!isVideoCall && isAiCall && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-violet-100 text-violet-700 border border-violet-200">
                              AI · CALL
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">
                          {(() => {
                            const raw = report.report_date || report.generated_at || report.created_at;
                            if (!raw) return '—';
                            const d = new Date(raw);
                            return Number.isNaN(d.getTime())
                              ? String(raw).slice(0, 10)
                              : d.toLocaleString('en-IN', {
                                  timeZone: 'Asia/Kolkata',
                                  day: '2-digit', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                });
                          })()}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 shrink-0 ${getStatusStyle(report.review_status)}`}>
                      {getStatusIcon(report.review_status)}
                      {(report.review_status || 'pending_review').replace('_', ' ')}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className={`px-2 py-1 rounded ${moodClass}`} data-testid="report-mood-pill">
                      Mood: {mood}
                    </span>
                    <span className={`px-2 py-1 rounded ${engagementClass}`} data-testid="report-engagement-pill">
                      Engagement: {engagementText}
                    </span>
                    <span className={`px-2 py-1 rounded ${growthClass}`} data-testid="report-growth-pill">
                      Growth Score: {growthLabel}
                    </span>
                  </div>

                  {report.principal_feedback && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-700">Principal Feedback</p>
                      <p className="text-sm text-blue-600">{report.principal_feedback}</p>
                    </div>
                  )}

                  <div className="mt-4 flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/observer/report-detail/${report.id}`)}
                      data-testid={`view-report-btn-${report.id}`}
                    >
                      View Full Report
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setInsightCardReport({
                        ...report,
                        ...(report.report_data || {}),
                      })}
                      className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white gap-1.5"
                      data-testid={`observer-insight-card-btn-${report.id}`}
                    >
                      <ImageIcon className="w-3.5 h-3.5" /> Insight Card
                    </Button>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        ))}
      </div>

      {/* Floating bulk action bar */}
      {activeTab === 'daily' && selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white shadow-2xl rounded-full px-5 py-3 flex items-center gap-4 border border-slate-700"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-medium" data-testid="bulk-selection-count">
            {selectedIds.size} selected
          </span>
          <div className="h-5 w-px bg-slate-700" />
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 gap-1.5"
            onClick={clearSelection}
            data-testid="bulk-cancel-btn"
          >
            <X className="w-4 h-4" /> Cancel
          </Button>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white h-8 gap-1.5"
            onClick={() => setConfirmOpen(true)}
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </Button>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="bulk-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedIds.size} report{selectedIds.size === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              These reports will be removed from every portal (Observer, Parent, Principal).
              Linked parent Insight Cards will be hidden too. Backups are kept in the archive —
              an admin can restore them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="bulk-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulkDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="bulk-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {insightCardReport && (
        <InsightCard
          report={insightCardReport}
          onClose={() => setInsightCardReport(null)}
          tokenKey="observer_token"
          endpoint={`${BACKEND_URL}/api/observer/insight-card/generate`}
        />
      )}
    </div>
  );
};

export default ObserverReportsList;
